/**
 * 接続線（Connection / Edge）の詳細編集 Drawer。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5（Phase 23a-ext-2 / 23b の前段）
 *
 * 編集項目:
 *   - trigger.kind: on_complete / on_form_submit / on_open / on_link_click / after_delay / manual
 *   - trigger.delayMs: after_delay 時のみ
 *   - condition.kind: always / has_tag / in_group / form_answered / not_form_answered
 *   - condition の各種フィールド（tag / groupId / formId / value 等）
 *   - label: 線の表示ラベル（任意）
 *
 * Phase 23b で発火が実装されると、この Drawer の設定が実行ロジックに反映される。
 */
import { httpsCallable } from 'firebase/functions';
import { ChevronDown, ChevronRight, Trash2, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useForms } from '@/hooks/useForms';
import { useMailGroups } from '@/hooks/useMailGroups';
import { functions } from '@/lib/firebase';

import { getFieldElements } from '@goenhub/shared';
import type {
  CampaignCondition,
  CampaignConditionKind,
  CampaignConnection,
  CampaignTrigger,
  CampaignTriggerKind,
  FieldFormElement,
} from '@goenhub/shared';

/** fieldId 未指定（回答有無のみで判定）を表す Select 用センチネル値。 */
const FIELD_NONE = '__none__';

/** 質問タイプの日本語ラベル（フィールド選択ドロップダウンの補助表示用）。 */
const FIELD_TYPE_LABELS: Partial<Record<FieldFormElement['fieldType'], string>> =
  {
    text: '記述',
    textarea: '記述（長文）',
    email: 'メール',
    tel: '電話',
    number: '数値',
    select: 'プルダウン',
    radio: '単一選択',
    checkbox: '複数選択',
    date: '日付',
    consent: '同意',
  };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: CampaignConnection | null;
  onSave: (next: CampaignConnection) => void;
  onDelete?: (id: string) => void;
  /** 手動発火ボタンを動作させるために必要 */
  campaignId?: string;
}

const TRIGGER_OPTIONS: Array<{
  value: CampaignTriggerKind;
  label: string;
  disabled?: boolean;
}> = [
  { value: 'on_complete', label: '前のカードが完了したとき' },
  { value: 'on_form_submit', label: 'フォームが送信されたとき' },
  { value: 'after_delay', label: '一定時間経過後' },
  { value: 'on_open', label: 'メール / ニュースが開封されたとき' },
  { value: 'on_link_click', label: 'リンクがクリックされたとき' },
  { value: 'on_event_start', label: 'イベント開催が開始したとき' },
  { value: 'on_event_end', label: 'イベント開催が終了したとき' },
  { value: 'manual', label: '手動実行（保存後に「今すぐ発火」ボタンから実行）' },
];

const CONDITION_OPTIONS: Array<{
  value: CampaignConditionKind;
  label: string;
}> = [
  { value: 'always', label: '常に通す（条件なし）' },
  { value: 'has_tag', label: '指定タグを持つ人だけ' },
  { value: 'in_group', label: '指定グループに所属する人だけ' },
  { value: 'form_answered', label: '指定フォームに回答済の人だけ' },
  { value: 'not_form_answered', label: '指定フォームに未回答の人だけ' },
];

// ─────────────────────────────────────────────
// Phase 23a-ext-3: プリセット 7 種
// 業務シーンに即した定型組合せ。「詳細設定」展開で個別編集も可能。
// ─────────────────────────────────────────────
type PresetId =
  | 'preset:on_complete'
  | 'preset:on_form_submit'
  | 'preset:after_delay'
  | 'preset:on_open'
  | 'preset:on_link_click'
  | 'preset:has_tag'
  | 'preset:not_form_answered'
  | 'preset:manual'
  | 'preset:custom';

interface PresetDef {
  id: PresetId;
  label: string;
  hint: string;
  trigger: CampaignTriggerKind;
  condition?: CampaignConditionKind;
  /** Phase 23e で実装予定。UI 上は disabled で表示する。 */
  disabled?: boolean;
}

const PRESETS: PresetDef[] = [
  {
    id: 'preset:on_complete',
    label: '完了したら次へ',
    hint: '前のカード（メール送信や LINE 配信）が完了 → そのまま次のカードへ',
    trigger: 'on_complete',
  },
  {
    id: 'preset:on_form_submit',
    label: 'フォーム送信されたら',
    hint: 'フォームに回答した人を次のカードへ流す',
    trigger: 'on_form_submit',
  },
  {
    id: 'preset:after_delay',
    label: 'N 日後に進む',
    hint: '上流が完了してから一定時間が経過したら次へ（数値は下のフィールドで指定）',
    trigger: 'after_delay',
  },
  {
    id: 'preset:has_tag',
    label: '特定タグの人だけ通す',
    hint: '前のカード完了 + 指定タグを持つ人だけ次へ',
    trigger: 'on_complete',
    condition: 'has_tag',
  },
  {
    id: 'preset:not_form_answered',
    label: '未回答者だけ通す',
    hint: '前のカード完了 + 指定フォームに未回答の人だけ次へ（リマインダー用）',
    trigger: 'on_complete',
    condition: 'not_form_answered',
  },
  {
    id: 'preset:on_open',
    label: 'メールが開封されたら',
    hint: '開封者だけを次のカードへ流す（メールに埋め込まれた計測ピクセル経由）',
    trigger: 'on_open',
  },
  {
    id: 'preset:on_link_click',
    label: 'リンクがクリックされたら',
    hint: 'CTA リンク先 LP に着地した人だけを次へ（cid 付き URL 経由で検知）',
    trigger: 'on_link_click',
  },
  {
    id: 'preset:manual',
    label: '手動で発火',
    hint: '保存後、接続線設定ドロワーの「今すぐ発火」ボタンから任意のタイミングで実行',
    trigger: 'manual',
  },
];

/** 現状の trigger / condition から最も近い preset を逆引き */
function inferPresetId(
  triggerKind: CampaignTriggerKind,
  conditionKind: CampaignConditionKind | undefined,
): PresetId {
  if (conditionKind === 'has_tag' && triggerKind === 'on_complete')
    return 'preset:has_tag';
  if (conditionKind === 'not_form_answered' && triggerKind === 'on_complete')
    return 'preset:not_form_answered';
  if (conditionKind && conditionKind !== 'always') return 'preset:custom';
  switch (triggerKind) {
    case 'on_complete':
      return 'preset:on_complete';
    case 'on_form_submit':
      return 'preset:on_form_submit';
    case 'after_delay':
      return 'preset:after_delay';
    case 'on_open':
      return 'preset:on_open';
    case 'on_link_click':
      return 'preset:on_link_click';
    case 'manual':
      return 'preset:manual';
    default:
      return 'preset:custom';
  }
}

function DelayInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (ms: number | undefined) => void;
}) {
  const [unit, setUnit] = useState<'sec' | 'min' | 'hour' | 'day'>(() => {
    if (!value || value <= 0) return 'min';
    if (value % (24 * 60 * 60 * 1000) === 0) return 'day';
    if (value % (60 * 60 * 1000) === 0) return 'hour';
    if (value % (60 * 1000) === 0) return 'min';
    return 'sec';
  });
  const factor = {
    sec: 1000,
    min: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
  }[unit];
  const [num, setNum] = useState<string>(() =>
    value && value > 0 ? String(value / factor) : '',
  );
  useEffect(() => {
    const n = parseFloat(num);
    if (Number.isFinite(n) && n > 0) {
      onChange(Math.round(n * factor));
    } else {
      onChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [num, factor]);

  return (
    <div>
      <Label className="text-xs">遅延時間</Label>
      <div className="flex gap-2">
        <Input
          type="number"
          min="0"
          step="any"
          value={num}
          onChange={(e) => setNum(e.target.value)}
          placeholder="0"
          className="flex-1"
        />
        <Select value={unit} onValueChange={(v) => setUnit(v as typeof unit)}>
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sec">秒</SelectItem>
            <SelectItem value="min">分</SelectItem>
            <SelectItem value="hour">時間</SelectItem>
            <SelectItem value="day">日</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function ConnectionDetailDrawer({
  open,
  onOpenChange,
  connection,
  onSave,
  onDelete,
  campaignId,
}: Props) {
  const [draft, setDraft] = useState<CampaignConnection | null>(connection);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMode, setManualMode] = useState<'all' | 'group' | 'tag'>('all');
  const [manualValue, setManualValue] = useState('');
  const [manualBusy, setManualBusy] = useState(false);

  const formsQ = useForms();
  const mailGroupsQ = useMailGroups();

  useEffect(() => {
    if (open && connection) {
      const initial = {
        ...connection,
        trigger: connection.trigger ?? { kind: 'on_complete' },
        condition: connection.condition,
      };
      setDraft(initial);
      // 既存の組合せが preset:custom 相当なら詳細を自動展開
      const inferred = inferPresetId(
        initial.trigger.kind,
        initial.condition?.kind,
      );
      setAdvancedOpen(inferred === 'preset:custom');
    }
  }, [open, connection]);

  if (!draft) return null;

  const currentPreset = inferPresetId(
    draft.trigger?.kind ?? 'on_complete',
    draft.condition?.kind,
  );

  const applyPreset = (presetId: PresetId) => {
    if (presetId === 'preset:custom') {
      // 何もしない（手動編集を促す）
      setAdvancedOpen(true);
      return;
    }
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const nextTrigger: CampaignConnection['trigger'] =
      preset.trigger === 'after_delay'
        ? { kind: 'after_delay', delayMs: draft.trigger?.delayMs ?? 0 }
        : { kind: preset.trigger };
    const nextCondition: CampaignConnection['condition'] | undefined =
      preset.condition && preset.condition !== 'always'
        ? ({
            kind: preset.condition,
            // 既存値を保持
            ...(draft.condition ?? {}),
          } as CampaignConnection['condition'])
        : undefined;
    setDraft({
      ...draft,
      trigger: nextTrigger,
      condition: nextCondition,
    });
  };

  const updateTrigger = (patch: Partial<CampaignTrigger>) => {
    setDraft({
      ...draft,
      trigger: { ...(draft.trigger ?? { kind: 'on_complete' }), ...patch },
    });
  };

  const updateCondition = (patch: Partial<CampaignCondition> | null) => {
    if (patch === null) {
      setDraft({ ...draft, condition: undefined });
      return;
    }
    setDraft({
      ...draft,
      condition: {
        kind: 'always',
        ...(draft.condition ?? {}),
        ...patch,
      } as CampaignCondition,
    });
  };

  const handleSave = () => {
    onSave(draft);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    if (!window.confirm('この接続線を削除しますか？')) return;
    onDelete(draft.id);
    onOpenChange(false);
  };

  /** 手動発火（trigger.kind === 'manual' の接続線のみ） */
  const handleManualTrigger = async () => {
    if (!campaignId) {
      toast.error(
        'campaignId が不明のため手動発火できません。保存後に再度お試しください。',
      );
      return;
    }
    if (
      (manualMode === 'group' || manualMode === 'tag') &&
      !manualValue.trim()
    ) {
      toast.error(
        manualMode === 'group'
          ? 'グループを選択してください'
          : 'タグを入力してください',
      );
      return;
    }
    setManualBusy(true);
    try {
      const fn = httpsCallable<unknown, { ok: boolean; audienceCount: number }>(
        functions,
        'triggerCampaignConnection',
      );
      const res = await fn({
        campaignId,
        connectionId: draft.id,
        audienceMode: manualMode,
        audienceValue: manualValue.trim() || undefined,
      });
      toast.success(
        `手動発火を実行しました（対象: ${res.data.audienceCount} 人）`,
      );
      setManualOpen(false);
    } catch (err) {
      toast.error(
        `手動発火に失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setManualBusy(false);
    }
  };

  const conditionKind: CampaignConditionKind =
    draft.condition?.kind ?? 'always';

  // form_answered 用: 選択中フォームの質問（field 要素）一覧。
  // フィールド ID を手入力させる代わりに、このリストから設問を選ばせる。
  const selectedForm = (formsQ.data ?? []).find(
    (f) => f.id === draft.condition?.formId,
  );
  const formFields: FieldFormElement[] = selectedForm
    ? getFieldElements(selectedForm.elements ?? [])
    : [];
  const currentFieldId = draft.condition?.fieldId ?? '';
  // 選択中の fieldId に対応する設問（値ドロップダウン化の判定に使う）。
  const selectedField = formFields.find((f) => f.id === currentFieldId);
  // レガシー / フォーム差し替えで、保存済み fieldId が現フォームの設問に無い場合。
  const orphanFieldId =
    currentFieldId && !selectedField ? currentFieldId : '';
  // select / radio / checkbox は候補を値ドロップダウンに出せる。
  const fieldOptions =
    selectedField &&
    (selectedField.fieldType === 'select' ||
      selectedField.fieldType === 'radio' ||
      selectedField.fieldType === 'checkbox')
      ? (selectedField.options ?? []).filter((o) => o.trim() !== '')
      : [];
  // exists / not_exists（回答の有無だけで判定）は値入力が不要。
  const conditionOperator = draft.condition?.operator ?? 'eq';
  const valueNeeded =
    conditionOperator !== 'exists' && conditionOperator !== 'not_exists';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>接続線の設定</DialogTitle>
          <DialogDescription>
            線の動作をプリセットから選びます。複雑な組合せが必要なら「詳細設定」を開いて個別編集してください。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* プリセット */}
          <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
            <Label className="text-xs font-semibold !mb-0">動作プリセット</Label>
            <div className="grid grid-cols-1 gap-1.5">
              {PRESETS.map((p) => {
                const active = currentPreset === p.id;
                const disabled = p.disabled === true;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      applyPreset(p.id);
                    }}
                    title={
                      disabled
                        ? 'このプリセットは現在実装されていません'
                        : undefined
                    }
                    className={`text-left rounded border px-3 py-2 transition-colors ${
                      disabled
                        ? 'border-[var(--border)] bg-[var(--bg-surface)] opacity-50 cursor-not-allowed'
                        : active
                          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                          : 'border-[var(--border)] bg-[var(--bg-surface)] hover:border-[var(--accent)]/60'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`text-[13px] font-medium ${
                          active
                            ? 'text-[var(--accent)]'
                            : 'text-[var(--text-primary)]'
                        }`}
                      >
                        {p.label}
                      </div>
                      {disabled && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] text-[var(--text-muted)] border border-[var(--border)]">
                          未実装
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      {p.hint}
                    </div>
                  </button>
                );
              })}
              {currentPreset === 'preset:custom' && (
                <div className="rounded border border-dashed border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2 text-[11px] text-[var(--accent-amber)]">
                  上のプリセットに当てはまらない組合せです（カスタム）。詳細設定で編集できます。
                </div>
              )}
            </div>

            {/* after_delay 専用の数値入力（プリセット選択中も表示） */}
            {draft.trigger?.kind === 'after_delay' && (
              <div className="pt-2">
                <DelayInput
                  value={draft.trigger.delayMs}
                  onChange={(ms) => updateTrigger({ delayMs: ms })}
                />
              </div>
            )}
          </div>

          {/* 詳細設定（折りたたみ） */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((s) => !s)}
            className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            {advancedOpen ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            詳細設定（trigger / condition を個別編集）
          </button>

          {advancedOpen && (
            <>
              {/* trigger */}
              <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-3">
                <Label className="text-xs font-semibold !mb-0">発火タイミング</Label>
                <Select
                  value={draft.trigger?.kind ?? 'on_complete'}
                  onValueChange={(v) =>
                    updateTrigger({ kind: v as CampaignTriggerKind })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        disabled={o.disabled === true}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {draft.trigger?.kind === 'after_delay' && (
                  <DelayInput
                    value={draft.trigger.delayMs}
                    onChange={(ms) => updateTrigger({ delayMs: ms })}
                  />
                )}
              </div>

              {/* condition */}
              <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-3">
                <Label className="text-xs font-semibold !mb-0">通過条件</Label>
                <Select
                  value={conditionKind}
                  onValueChange={(v) => {
                    if (v === 'always') {
                      updateCondition(null);
                    } else {
                      updateCondition({ kind: v as CampaignConditionKind });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

            {conditionKind === 'has_tag' && (
              <div>
                <Label className="text-xs">タグ名</Label>
                <Input
                  type="text"
                  value={draft.condition?.tag ?? ''}
                  onChange={(e) => updateCondition({ tag: e.target.value })}
                  placeholder="例: 4月キャンペーン応募者"
                />
              </div>
            )}

            {conditionKind === 'in_group' && (
              <div>
                <Label className="text-xs">配信先グループ</Label>
                <Select
                  value={draft.condition?.groupId ?? ''}
                  onValueChange={(v) => updateCondition({ groupId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    {(mailGroupsQ.data ?? []).map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(conditionKind === 'form_answered' ||
              conditionKind === 'not_form_answered') && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">対象フォーム</Label>
                  <Select
                    value={draft.condition?.formId ?? ''}
                    onValueChange={(v) =>
                      // フォームを切り替えたら、旧フォームの設問 ID / 値をクリア
                      updateCondition({
                        formId: v,
                        fieldId: undefined,
                        value: undefined,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {(formsQ.data ?? []).map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {conditionKind === 'form_answered' && (
                  <>
                    <div>
                      <Label className="text-xs">
                        設問で絞り込む（任意）
                      </Label>
                      <Select
                        value={currentFieldId || FIELD_NONE}
                        onValueChange={(v) =>
                          updateCondition({
                            fieldId: v === FIELD_NONE ? undefined : v,
                            // 設問を変えたら値もリセット（選択肢が変わるため）
                            value: undefined,
                          })
                        }
                        disabled={!draft.condition?.formId}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              draft.condition?.formId
                                ? '設問を選択'
                                : '先にフォームを選択'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={FIELD_NONE}>
                            指定なし（回答の有無だけで判定）
                          </SelectItem>
                          {formFields.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.label || '(無題の設問)'}
                              {FIELD_TYPE_LABELS[f.fieldType]
                                ? `（${FIELD_TYPE_LABELS[f.fieldType]}）`
                                : ''}
                            </SelectItem>
                          ))}
                          {/* レガシー / 差し替えで現フォームに存在しない ID を保持している場合の救済 */}
                          {orphanFieldId && (
                            <SelectItem value={orphanFieldId}>
                              不明な設問（{orphanFieldId}）
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {draft.condition?.formId && formFields.length === 0 && (
                        <p className="text-[10px] text-[var(--text-muted)] mt-1">
                          このフォームには回答設問がありません。「指定なし」で回答の有無だけを判定します。
                        </p>
                      )}
                    </div>

                    {/* 設問を選んだときだけ 演算子（+ 必要なら値）を表示 */}
                    {currentFieldId && (
                      <>
                        <div className={valueNeeded ? 'grid grid-cols-2 gap-2' : ''}>
                          <div>
                            <Label className="text-xs">条件</Label>
                            <Select
                              value={conditionOperator}
                              onValueChange={(v) =>
                                updateCondition({
                                  operator:
                                    v as CampaignCondition['operator'],
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="exists">
                                  回答がある（入力あり）
                                </SelectItem>
                                <SelectItem value="not_exists">
                                  回答がない（空欄）
                                </SelectItem>
                                <SelectItem value="eq">＝（一致）</SelectItem>
                                <SelectItem value="neq">≠（不一致）</SelectItem>
                                <SelectItem value="contains">含む</SelectItem>
                                <SelectItem value="gt">＞（より大きい）</SelectItem>
                                <SelectItem value="lt">＜（より小さい）</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {valueNeeded && (
                            <div>
                              <Label className="text-xs">値</Label>
                              {fieldOptions.length > 0 ? (
                                // select / radio / checkbox は候補から選ばせる
                                <Select
                                  value={draft.condition?.value ?? ''}
                                  onValueChange={(v) =>
                                    updateCondition({ value: v })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="選択肢から選ぶ" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {fieldOptions.map((opt) => (
                                      <SelectItem key={opt} value={opt}>
                                        {opt}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type="text"
                                  value={draft.condition?.value ?? ''}
                                  onChange={(e) =>
                                    updateCondition({ value: e.target.value })
                                  }
                                  placeholder="例: はい"
                                />
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-[10px] text-[var(--text-muted)]">
                          {conditionOperator === 'exists'
                            ? `「${selectedField?.label || currentFieldId}」に回答した（入力がある）人だけを次へ流します。`
                            : conditionOperator === 'not_exists'
                              ? `「${selectedField?.label || currentFieldId}」が空欄（未入力）の人だけを次へ流します。`
                              : `「${selectedField?.label || currentFieldId}」の回答が上の条件を満たす人だけを次へ流します。`}
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
              </div>
            </>
          )}

          {/* label（プリセット選択時も常に表示） */}
          <div>
            <Label className="text-xs">線のラベル（任意）</Label>
            <Input
              type="text"
              value={draft.label ?? ''}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="未指定ならプリセット名を自動表示"
            />
          </div>

          {/* manual トリガーのときだけ「今すぐ発火」ボタンを表示 */}
          {draft.trigger?.kind === 'manual' && campaignId && (
            <div className="rounded border border-[var(--accent)] bg-[var(--accent-subtle)] p-3 space-y-2">
              <Label className="text-xs font-semibold !mb-0 text-[var(--accent)]">
                手動発火
              </Label>
              <p className="text-[11px] text-[var(--text-secondary)]">
                この接続線を今すぐ実行します。保存済みでないと使えません。
              </p>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setManualOpen(true)}
                className="w-full"
              >
                <Zap className="size-3.5 mr-1" />
                今すぐ発火
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="!justify-between">
          {onDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="text-[var(--accent-red)]"
            >
              <Trash2 className="size-3.5 mr-1" />
              削除
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
            >
              適用
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* 手動発火ダイアログ */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>手動発火 — 対象を指定</DialogTitle>
            <DialogDescription>
              この接続線を実行する対象（audience）を指定してください。指定後、接続線の条件（condition）でさらに絞り込まれます。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">対象モード</Label>
              <Select
                value={manualMode}
                onValueChange={(v) => {
                  setManualMode(v as 'all' | 'group' | 'tag');
                  setManualValue('');
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全配信先</SelectItem>
                  <SelectItem value="group">指定グループ</SelectItem>
                  <SelectItem value="tag">指定タグ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {manualMode === 'group' && (
              <div>
                <Label className="text-xs">グループ</Label>
                <Select
                  value={manualValue}
                  onValueChange={(v) => setManualValue(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    {(mailGroupsQ.data ?? []).map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {manualMode === 'tag' && (
              <div>
                <Label className="text-xs">タグ名</Label>
                <Input
                  type="text"
                  value={manualValue}
                  onChange={(e) => setManualValue(e.target.value)}
                  placeholder="例: 4月キャンペーン応募者"
                />
              </div>
            )}
            <div className="rounded border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2 text-[11px] text-[var(--accent-amber)]">
              実行後の取り消しはできません。 condition による絞り込みは別途適用されます。
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setManualOpen(false)}
              disabled={manualBusy}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleManualTrigger}
              disabled={manualBusy}
            >
              {manualBusy ? '実行中...' : '今すぐ発火'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
